import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { PhnxGriditem } from '@/components/01-atoms/Griditem/Griditem'
import { PhnxImageHolder } from '@/components/01-atoms/ImageHolder/ImageHolder'
import { PhnxBrucebanner } from '@/components/02-molecules/Brucebanner/Brucebanner'
import { PhnxCard } from '@/components/02-molecules/Card/Card'
import { PhnxGridpanel } from '@/components/02-molecules/Gridpanel/Gridpanel'
import { PhnxHerobanner } from '@/components/02-molecules/Herobanner/Herobanner'
import { PhnxReviewslider } from '@/components/02-molecules/Reviewslider/Reviewslider'
import { PhnxRelatedArticles } from '@/components/03-organisms/Relatedarticles/RelatedArticles'
import { isValidCardLink } from '@/lib/link'

import styles from './Service.module.css'

export type ServiceProps = {
  title?: string
  subtitle?: string
  description?: string
  heroAsset?: {
    src: string
    alt: string
    focus?: string
    washOpacity: number
  }
  heroButton?: React.ReactNode
  frameSrc?: string
  heroForm?: React.ReactNode
  reviews?: {
    rating?: number
    content?: React.ReactNode
    author?: string
  }[]
  featureContent?: React.ReactNode
  steps?: {
    title: string
    description: React.ReactNode
    asset?: {
      src?: string
    }
  }[]
  stepsTitle?: string
  services?: {
    title?: string
    description?: string
    body: React.ReactNode
    link?: {
      url: string
      label?: string
    }
    asset?: {
      src?: string
      alt?: string
      focus?: string
    }
  }[]
  serviceItemsPerRow?: 'one' | 'two' | 'three' | 'four'
  servicesTitle?: string
  servicesSubtitle?: string
  cta?: React.ReactNode
  relatedArticles?: {
    title?: string
    tags?: string[]
    author?: React.ReactNode
    asset?: {
      src?: string
      alt?: string
      focus?: string
    }
    link?: string
  }[]
  relatedArticlesTitle?: string
  relatedArticlesSubtitle?: string
  belowContent: React.ReactNode
}

export const PhnxService: React.FC<ServiceProps> = ({
  title,
  subtitle,
  description,
  heroAsset,
  frameSrc,
  heroForm,
  reviews,
  featureContent,
  steps,
  stepsTitle = 'HOW IT WORKS',
  services,
  serviceItemsPerRow,
  servicesTitle = 'OUR SERVICES',
  servicesSubtitle = 'See our range of Accounting and Business Advisory offerings.',
  cta,
  relatedArticles,
  relatedArticlesTitle = 'RELATED ARTICLES',
  relatedArticlesSubtitle = 'Explore our Accounting and Business Advisory insights.',
  heroButton,
  belowContent,
}) => {
  const stepsToPass =
    steps != null && steps.length > 0
      ? steps.map((step, index) => (
          <PhnxGriditem
            key={index}
            asset={{
              filename:
                step.asset != null && step.asset.src != null
                  ? step.asset.src
                  : `/assets/no${index + 1}.webp`,
              alt: '',
              layout: 'xsmall',
            }}
            body={
              <>
                {step.title != null && step.title !== '' && (
                  <p>
                    <strong>{step.title}</strong>
                  </p>
                )}
                {step.description != null && <p>{step.description}</p>}
              </>
            }
          />
        ))
      : []

  const itemsServices =
    services != null && services.length > 0
      ? services.map((service, index) => {
          // Process the link URL safely; treat empty/homepage as no link (e.g. NZ without link)
          let processedUrl = ''
          if (service.link?.url != null && service.link.url !== '') {
            const url = service.link.url
            if (url.startsWith('http')) {
              processedUrl = url
            } else if (url.startsWith('/en-nz/')) {
              processedUrl = url.replace('/en-nz/', '/')
            } else if (!url.startsWith('/')) {
              processedUrl = `/${url}`
            } else {
              processedUrl = url
            }
          }
          const hasValidLink = isValidCardLink(processedUrl)

          return (
            <PhnxCard
              key={index}
              alignment="default"
              title={service.title ?? ''}
              description={service.description ?? ''}
              body={service.body ?? ''}
              button={
                hasValidLink && service?.link?.label != null && service.link.label !== '' ? (
                  <PhnxButton
                    type="link"
                    variant="quinary"
                    label={service.link.label}
                    href={processedUrl}
                  />
                ) : null
              }
              asset={
                service.asset != null ? (
                  <PhnxImageHolder
                    images={[
                      {
                        src: service.asset.src ?? '',
                        alt: service.asset.alt ?? '',
                        focus: service.asset.focus ?? '',
                      },
                    ]}
                  />
                ) : null
              }
              link={hasValidLink ? processedUrl : undefined}
              layout="vertical"
              variant="septenary"
            />
          )
        })
      : []
  return (
    <div className={styles['service']}>
      <div className="header">
        {((frameSrc != null && frameSrc !== '') || heroForm != null) && (
          <PhnxBrucebanner
            title={title}
            subtitle={subtitle}
            description={description}
            asset={{
              src: heroAsset?.src ?? '',
              alt: heroAsset?.alt ?? '',
              focus: heroAsset?.focus ?? '',
              washOpacity: heroAsset?.washOpacity ?? 0.5,
            }}
            frameSrc={frameSrc}
            heroForm={heroForm}
            content={heroButton}
          />
        )}

        {(frameSrc == null || frameSrc === '') && heroForm == null && (
          <PhnxHerobanner
            title={title}
            subtitle={subtitle}
            asset={{
              src: heroAsset?.src ?? '',
              alt: heroAsset?.alt ?? '',
              focus: heroAsset?.focus ?? '',
              wash_opacity: 0.5,
            }}
            button={heroButton}
            layout="service"
          />
        )}
      </div>

      {reviews != null && reviews.length > 0 && (
        <div className="review-slider">
          <PhnxReviewslider title="What our clients say about us" reviews={reviews} />
        </div>
      )}

      {featureContent}
      {steps != null && steps.length > 0 && (
        <PhnxGridpanel
          cssStyle="service-steps"
          preTitle={stepsTitle}
          itemsPerRow="four"
          backgroundColor="#f2f5f6"
          padding={{
            unit: 'rem',
            default: true,
          }}
          items={stepsToPass}
          featureVariant={'primary'}
        />
      )}
      {services != null && services.length > 0 && (
        <PhnxGridpanel
          preTitle={servicesTitle}
          title={servicesSubtitle}
          itemsPerRow={serviceItemsPerRow || 'three'}
          alignment="center"
          padding={{
            unit: 'rem',
            default: true,
          }}
          items={itemsServices}
        />
      )}

      {cta}
      {relatedArticles != null && relatedArticles.length > 0 && (
        <div className="container">
          <PhnxRelatedArticles
            title={relatedArticlesTitle}
            subtitle={relatedArticlesSubtitle}
            layout="quaternary"
            button={
              <PhnxButton
                type="link"
                variant="tertiary"
                label="View all articles"
                href="/insights/articles"
              />
            }
            padding={{
              unit: 'rem',
              default: true,
            }}
            items={[
              relatedArticles != null && relatedArticles.length > 0
                ? relatedArticles.map((article, index) => (
                    <PhnxCard
                      key={index}
                      variant="secondary"
                      layout="vertical"
                      alignment="default"
                      style="square"
                      tags={article.tags}
                      link={article.link}
                      title={article.title ?? ''}
                      author={article.author}
                      asset={
                        <PhnxImageHolder
                          images={[
                            {
                              src: article.asset?.src ?? '',
                              alt: article.asset?.alt ?? '',
                              focus: article.asset?.focus ?? '',
                            },
                          ]}
                        />
                      }
                    />
                  ))
                : null,
            ]}
          />
        </div>
      )}

      {belowContent}
    </div>
  )
}
